import React, { useState } from "react";
import {
  Box,
  Menu,
  MenuItem,
  IconButton,
  ListItemText,
  ListItemIcon,
  Tooltip,
  Typography,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import CheckIcon from "@mui/icons-material/Check";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { RootState, useAppDispatch } from "../../../redux/store";
import { setLanguage } from "../../../redux/slices/authSlice";
import { LANGUAGES, nameToCode, resolveLanguage } from "../../../i18n/languages";
import { updateRetailerLanguage } from "../../../redux/apis/retailer/languageApis";

/**
 * Retailer-only language switcher shown in the navbar. Selecting a language:
 *  - updates the i18n active language (static UI text)
 *  - persists the choice in the auth slice + localStorage (drives the
 *    retailer-only `language` request header)
 *  - calls the retailer updateLanguage API
 * It renders nothing for non-retailer roles, so sales/admin are unaffected.
 */
export default function LanguageSelector() {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const role = useSelector((state: RootState) => state.auth.role);
  const currentLanguage = useSelector((state: RootState) => state.auth.language);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  if (role !== "retailer") return null;

  const current = resolveLanguage(currentLanguage);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = async (name: string) => {
    handleClose();
    if (name === current.name) return;

    // Persist the choice first (Redux + localStorage) so it survives the reload
    // and the new `language` header is used on every subsequent request.
    dispatch(setLanguage(name));
    i18n.changeLanguage(nameToCode(name));

    try {
      await updateRetailerLanguage(name);
    } catch (error) {
      // Keep the selected language; just surface the sync failure.
      toast.error("Failed to update language");
      // eslint-disable-next-line no-console
      console.error("updateRetailerLanguage failed:", error);
    }

    // Hard refresh so all pages re-fetch their data with the new language
    // header (backend returns language-specific content) and re-render fully.
    window.location.reload();
  };

  return (
    <Box>
      <Tooltip title={current.label}>
        <IconButton
          onClick={handleOpen}
          size="small"
          color="primary"
          aria-label="select language"
          sx={{
            "&:hover": { backgroundColor: "rgba(60, 119, 149, 0.08)" },
          }}
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: { mt: 1.5, minWidth: 200, borderRadius: 2 },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {LANGUAGES.map((lang) => {
          const selected = lang.name === current.name;
          return (
            <MenuItem
              key={lang.code}
              selected={selected}
              onClick={() => handleSelect(lang.name)}
              sx={{ gap: 1 }}
            >
              <ListItemText
                primary={
                  <Typography fontSize={14} fontWeight={selected ? 600 : 400}>
                    {lang.nativeLabel}
                  </Typography>
                }
                secondary={
                  lang.nativeLabel !== lang.label ? (
                    <Typography fontSize={11} color="text.secondary">
                      {lang.label}
                    </Typography>
                  ) : undefined
                }
              />
              {selected && (
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <CheckIcon fontSize="small" color="primary" />
                </ListItemIcon>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
}
